import { client } from './client'
import { statusFilterRequest } from './statusFilterRequest'
import { parseContentDispositionFilename } from '@/lib/downloadBlob'
import type { Intent, IntentDetail, IntentFilters, PaginationParams, PagedResult, CreateIntentRequest, UpdateIntentRequest } from '@/types'

export interface BulkDeleteIntentsResponse {
  deletedIds: string[]
  skippedIds: string[]
}

export const intentsApi = {
  getAll: (projectId: string, params?: Partial<PaginationParams> & IntentFilters) =>
    client.get<PagedResult<Intent>>(`/projects/${projectId}/intents`, statusFilterRequest(params)).then(r => r.data),

  getById: (projectId: string, id: string) =>
    client.get<IntentDetail>(`/projects/${projectId}/intents/${id}`).then(r => r.data),

  getFeatures: (projectId: string) =>
    client.get<string[]>(`/projects/${projectId}/intents/features`).then(r => r.data),

  getEpics: (projectId: string) =>
    client.get<string[]>(`/projects/${projectId}/intents/epics`).then(r => r.data),

  create: (projectId: string, request: CreateIntentRequest) =>
    client.post(`/projects/${projectId}/intents`, request).then(r => r.data),

  update: (projectId: string, id: string, data: UpdateIntentRequest) =>
    client.put(`/projects/${projectId}/intents/${id}`, data).then(r => r.data),

  delete: (projectId: string, id: string) =>
    client.delete(`/projects/${projectId}/intents/${id}`).then(r => r.data),

  bulkDelete: (projectId: string, ids: string[]) =>
    client.delete<BulkDeleteIntentsResponse>(`/projects/${projectId}/intents`, { data: { intentIds: ids } }).then(r => r.data),

  exportCsv: async (projectId: string, intentIds: string[], columns: string[]): Promise<{ blob: Blob; fileName: string }> => {
    const response = await client.post<Blob>(
      `/projects/${projectId}/intents/export-csv`,
      { intentIds, columns },
      { responseType: 'blob' },
    )
    const fileName = parseContentDispositionFilename(
      response.headers['content-disposition'] as string | undefined,
      'intents.csv',
    )
    return { blob: response.data, fileName }
  },

  exportAllCsv: async (projectId: string, filters: IntentFilters, columns: string[]): Promise<{ blob: Blob; fileName: string }> => {
    const response = await client.post<Blob>(
      `/projects/${projectId}/intents/export-csv/all`,
      { ...filters, columns },
      { responseType: 'blob' },
    )
    const fileName = parseContentDispositionFilename(
      response.headers['content-disposition'] as string | undefined,
      'intents.csv',
    )
    return { blob: response.data, fileName }
  },
}
